import { err, ok, type Result } from 'neverthrow';
import type { Graph, NodeId } from '../../../types/graph';
import { OptionType } from '@morph-mapper/types';
import {
  CommandErrorKind,
  CommandKind,
  type CommandResult,
  type CommandError,
  type GraphCommand,
  EffectBuilder,
} from '../types';
import { effectsForOptionChange, withEffects } from '../effects';
import { bumpGraph } from '../utils';

export class UpdateNodeOptionValueCommand implements GraphCommand {
  readonly kind = CommandKind.UpdateNodeOptionValue;

  constructor(
    public readonly nodeId: NodeId,
    public readonly optionKey: string,
    public readonly value: unknown
  ) {}

  apply(graph: Graph): Result<CommandResult, CommandError> {
    const node = graph.nodes[this.nodeId];
    if (!node) {
      return err({
        kind: CommandErrorKind.NotFound,
        message: `Node ${this.nodeId} not found`,
      });
    }

    const previous = node.options[this.optionKey];

    let previousValue = null;
    if (previous.type === OptionType.Graph) {
      previousValue = previous.graphId;
    } else {
      previousValue = previous.value;
    }

    let nextOption;
    if (previous.type === OptionType.Graph) {
      nextOption = {
        type: previous.type,
        graphId: typeof this.value === 'string' ? this.value : undefined,
        visibility: previous.visibility,
      };
    } else {
      nextOption = {
        type: previous.type,
        value: this.value,
        visibility: previous.visibility,
      };
    }

    const nextGraph: Graph = {
      ...graph,
      nodes: {
        ...graph.nodes,
        [this.nodeId]: {
          ...node,
          options: {
            ...node.options,
            [this.optionKey]: nextOption,
          },
        },
      },
    };

    return ok({
      graph: bumpGraph(nextGraph),
      inverse: new UpdateNodeOptionValueCommand(
        this.nodeId,
        this.optionKey,
        previousValue
      ),
      dirty: {
        nodes: [this.nodeId],
        edges: [],
        topologyChanged: false,
      },
    });
  }
}

export const createUpdateNodeOptionValueCommand = (
  nodeId: NodeId,
  optionKey: string,
  value: unknown
) => {
  const command = new UpdateNodeOptionValueCommand(nodeId, optionKey, value);
  const buildEffects: EffectBuilder = ({ before, after }) => {
    const beforeOption = before.nodes[nodeId]?.options[optionKey];
    const afterOption = after.nodes[nodeId]?.options[optionKey];
    return effectsForOptionChange(beforeOption, afterOption);
  };

  return withEffects(command, buildEffects, ({ inverse }) =>
    withEffects(inverse, buildEffects)
  );
};
